// src/modules/Rules/components/interaction/DropdownActions.tsx
import { faEllipsisH, faPen, faTrash, faCheck, faXmark } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@shared/hooks/useMobile';
import React from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';

interface Props {
  /** Invocado para activar/desactivar la regla */
  onEdit?: () => void;
  /** Invocado para eliminar la regla */
  onDelete?: () => void;
  /** Opcional: evita clicks mientras está cargando */
  disabled?: boolean;
  /** Estado actual de la regla */
  isActive?: boolean;
  /** Handler para activar/desactivar */
  onToggleActive?: () => void;
  /** Permisos para editar */
  canEdit?: boolean;
  /** Permisos para eliminar */
  canDelete?: boolean;
}

export const DropdownActions: React.FC<Props> = ({
  onEdit,
  onDelete,
  disabled = false,
  isActive,
  onToggleActive,
  canEdit = true,
  canDelete = true,
}) => {
  const { t } = useTranslation('rulesModule');
  const isMobile = useIsMobile();
  const [open, setOpen] = React.useState(false);

  // Si no tiene ningún permiso, no mostrar el dropdown
  if (!canEdit && !canDelete) return null;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          className="p-2 rounded hover:bg-muted"
          data-testid="dropdown-trigger"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <FontAwesomeIcon icon={faEllipsisH} className="text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-44 border-card-border bg-popover" data-testid="dropdown-content">
        {/* Editar - requiere shipping.rules.edit */}
        {canEdit && (
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              if (onEdit) onEdit();
              setOpen(false);
            }}
            onMouseDown={(e) => e.stopPropagation()}
            disabled={disabled}
            className={disabled ? 'opacity-50 pointer-events-none' : ''}
          >
            <FontAwesomeIcon icon={faPen} className="mr-2 w-3.5" />
            {t('table.edit')}
          </DropdownMenuItem>
        )}

        {/* Eliminar - requiere shipping.rules.delete */}
        {canDelete && (
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              if (onDelete) onDelete();
              setOpen(false);
            }}
            onMouseDown={(e) => e.stopPropagation()}
            disabled={disabled}
            className={disabled ? 'opacity-50 pointer-events-none' : ''}
          >
            <FontAwesomeIcon icon={faTrash} className="mr-2 w-3.5" />
            {t('table.delete')}
          </DropdownMenuItem>
        )}

        {/* Activar/Desactivar solo en mobile - requiere shipping.rules.edit */}
        {isMobile && canEdit && (
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              if (onToggleActive) onToggleActive();
              setOpen(false);
            }}
            onMouseDown={(e) => e.stopPropagation()}
            disabled={disabled}
            className={disabled ? 'opacity-50 pointer-events-none' : ''}
          >
            {isActive ? (
              <>
                <FontAwesomeIcon icon={faXmark} className="w-3 h-3" />
                {t('table.deactivate')}
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faCheck} className="w-3 h-3" />
                {t('table.activate')}
              </>
            )}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
